import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

export interface PackingOpenPo {
  id: string;
  orderNumber: number;
  payeeId: string | null;
  payeeName: string | null;
  locationId: string;
  locationName: string;
  status: string;
  sourceType: string;
  purpose: string | null;
  createdByName: string | null;
  createdAt: string;
  itemCount: number;
}

export interface ConsolidatedPoItem {
  productId: string;
  productName: string;
  productBarcode: string | null;
  unitOfMeasure: string;
  totalQuantity: number;
  breakdown: {
    poId: string;
    orderNumber: number;
    locationId: string;
    locationName: string;
    quantity: number;
  }[];
}

export interface ConsolidatedPo {
  warehouseLocationId: string;
  warehouseLocationName: string;
  generatedAt: string;
  pos: {
    id: string;
    orderNumber: number;
    payeeName: string | null;
    locationId: string;
    locationName: string;
    status: string;
    createdAt: string;
  }[];
  items: ConsolidatedPoItem[];
  totalProducts: number;
  totalQuantity: number;
}

// Open POs across all non-warehouse locations — the candidate list the
// packing team confirms before generating the consolidated PO.
export function usePackingOpenPos(warehouseLocationId: string | undefined) {
  return useQuery({
    queryKey: ['packing-open-pos', warehouseLocationId],
    queryFn: () =>
      api.get<{ data: PackingOpenPo[] }>('/purchase-orders/packing/open', {
        warehouseLocationId: warehouseLocationId!,
      }),
    enabled: !!warehouseLocationId,
  });
}

// Read-only merge of the confirmed POs. POST because the id list can be long,
// but nothing is persisted server-side, so it's modeled as a query keyed on
// the sorted id set (print page and preview share the cache entry).
export function useConsolidatedPo(warehouseLocationId: string | undefined, poIds: string[] | null) {
  const sortedIds = poIds ? [...poIds].sort() : null;
  return useQuery({
    queryKey: ['packing-consolidated', warehouseLocationId, sortedIds],
    queryFn: () =>
      api.post<{ data: ConsolidatedPo }>('/purchase-orders/packing/consolidated', {
        warehouseLocationId,
        poIds: sortedIds,
      }),
    enabled: !!warehouseLocationId && !!sortedIds && sortedIds.length > 0,
  });
}
